import { FC, useMemo, useState, type JSX } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { actions as usersActions } from '@/api/users';
import * as customerActions from '@payaca/store/customer/customerActions';
import * as dealsActions from '@payaca/store/deals/dealsActions';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as jobsActions from '@payaca/store/jobs/jobsActions';

import EditJobPreviewButton from '@/ui/components/editJobPreviewButton/EditJobPreviewButton';
import EditJobSaveFeedback from '@/ui/components/editJobSaveFeedback/EditJobSaveFeedback';
import EditProtoInvoiceControl from '@/ui/components/editProtoInvoiceControl/EditProtoInvoiceControl';
import PreviewAndSendProtoInvoiceControl from '@/ui/components/previewAndSendProtoInvoiceControl/PreviewAndSendProtoInvoiceControl';
import ProtoInvoiceSidePanel from '@/ui/components/protoInvoiceSidePanel/ProtoInvoiceSidePanel';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import EditProposalLineItemSidebar from '@/ui/components/addEditProposalLineItem/EditProposalLineItemSidebar';
import { Modal } from '@payaca/ambrosia';
import { getUrlSearchParam } from '@payaca/helpers/urlHelper';
import { useDeal, useProposal } from '@payaca/store/hooks/appState';
import { getUploads } from '@payaca/store/uploads/uploadsActions';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './EditProtoInvoicePage.sass';

enum ViewType {
  EDIT = 'edit',
  SEND = 'send',
}

type Props = {
  protoInvoiceId: number;
  projectId: string;
  viewType?: ViewType;
};

const EditProtoInvoicePage: FC<Props> = ({
  protoInvoiceId,
  projectId,
  viewType,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [view, setView] = useState<ViewType>(viewType || ViewType.EDIT);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
  }>();

  const match = useRouteMatch();
  const isEditProposalLineItemDrawOpen = useRouteMatch<{
    proposalLineItemId: string;
  }>(`${match.url}/items/:proposalLineItemId`);

  const protoInvoice = useProposal(protoInvoiceId);

  const deal = useDeal(protoInvoice?.dealId);

  // can ignore bounced here... this proposal will only ever get marked as sent.
  // Any bounce handling will happen on the sent invoice.

  useEffect(() => {
    if (
      !!protoInvoice?.sentAt ||
      !!protoInvoice?.archivedAt ||
      !!protoInvoice?.inactivatedAt
    ) {
      history.push(`/projects/${projectId}`);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'history', and 'projectId'
  }, [
    protoInvoice?.sentAt,
    protoInvoice?.archivedAt,
    protoInvoice?.inactivatedAt,
  ]);

  const navigateToView = useMemo(() => {
    return getUrlSearchParam(location.search, 'view');
  }, [location]);

  useEffect(() => {
    if (
      navigateToView === ViewType.SEND &&
      validationResult &&
      validationResult.isValid
    ) {
      setView(ViewType.SEND);
    }
  }, [navigateToView, validationResult]);

  useEffect(() => {
    dispatch(
      jobsActions.requestGetJobValidationResult(
        protoInvoiceId,
        (validationResult) => {
          setValidationResult(validationResult);
        }
      )
    );
    dispatch(usersActions.getBusinessAttachments());

    return () => {
      dispatch(jobsActions.clearJobUpdateResults());
      dispatch(jobContentActions.clearJobLineItemUpdateResults());
      dispatch(jobContentActions.clearJobLineItemGroupUpdateResults());
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'protoInvoiceId', and 'dispatch'
  }, []);

  useEffect(() => {
    if (protoInvoiceId) {
      dispatch(jobsActions.requestGetJob(protoInvoiceId));
      dispatch(jobsActions.requestGetJobAttachmentsForJob(protoInvoiceId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [protoInvoiceId]);

  useEffect(() => {
    if (protoInvoice?.jobContentId) {
      dispatch(
        jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
          protoInvoice.jobContentId
        )
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [protoInvoice?.jobContentId]);

  useEffect(() => {
    if (protoInvoice?.invoiceConfig?.attachmentUploadIds?.length) {
      dispatch(
        getUploads.request({
          uploadIds: protoInvoice.invoiceConfig.attachmentUploadIds,
        })
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'protoInvoice.invoiceConfig.attachmentUploadIds', and 'dispatch'
  }, [protoInvoice?.invoiceConfig?.attachmentUploadIds?.length]);

  useEffect(() => {
    if (protoInvoice?.dealId) {
      dispatch(dealsActions.requestGetDeal(protoInvoice?.dealId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [protoInvoice?.dealId]);

  useEffect(() => {
    if (deal?.customerId) {
      dispatch(customerActions.requestGetCustomer(deal?.customerId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [deal?.customerId]);

  const handleProposalLineItemPersist = () => {
    if (protoInvoice) {
      dispatch(
        jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
          protoInvoice.jobContentId
        )
      );
    }

    if (isEditProposalLineItemDrawOpen?.params.proposalLineItemId !== 'new') {
      dispatch(
        jobContentActions.requestGetJobLineItemAttachmentsForJobLineItem(
          +isEditProposalLineItemDrawOpen!.params.proposalLineItemId
        )
      );
    }

    history.push(match.url);
  };

  return (
    <>
      <AuthenticatedPageWrapper
        className="edit-proto-invoice-page"
        breadcrumbItems={
          protoInvoice
            ? [
                {
                  label: 'Back',
                  to: `/projects/${projectId}/invoices-and-payments`,
                },
                { label: 'Edit invoice' },
              ]
            : undefined
        }
      >
        <div className="p-4">
          {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values */}
          <div className="grid @screen-lg:grid-cols-[1fr_25rem]">
            <div>
              {protoInvoice && (
                <>
                  <EditProtoInvoiceControl protoInvoiceId={protoInvoiceId} />
                </>
              )}
              <Modal
                isOpen={view === ViewType.SEND}
                title="Send invoice"
                size="lg"
                onClose={() => setView(ViewType.EDIT)}
              >
                <Modal.Body>
                  <PreviewAndSendProtoInvoiceControl
                    protoInvoiceId={protoInvoiceId}
                  />
                </Modal.Body>
              </Modal>
            </div>
            <div className="p-4 @screen-lg:sticky @screen-lg:top-0 @screen-lg:self-start">
              <div className="edit-proto-invoice-sidebar">
                <EditJobPreviewButton
                  jobId={protoInvoiceId}
                  proceedToPreview={() => {
                    dispatch(jobsActions.requestGetJob(protoInvoiceId));
                    setView(ViewType.SEND);
                  }}
                />
                <EditJobSaveFeedback jobId={protoInvoiceId} />
                <ProtoInvoiceSidePanel protoInvoiceId={protoInvoiceId} />
              </div>
            </div>
          </div>
        </div>
      </AuthenticatedPageWrapper>

      <EditProposalLineItemSidebar
        rootPage="invoice"
        isOpen={!!isEditProposalLineItemDrawOpen}
        proposalLineItemId={
          isEditProposalLineItemDrawOpen?.params.proposalLineItemId &&
          isEditProposalLineItemDrawOpen.params.proposalLineItemId !== 'new'
            ? isEditProposalLineItemDrawOpen.params.proposalLineItemId
            : undefined
        }
        onSaveSuccess={handleProposalLineItemPersist}
        onRemoveSuccess={handleProposalLineItemPersist}
        onClose={() => history.push(match.url)}
      />
    </>
  );
};
export default EditProtoInvoicePage;
